import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import serviceClassificationService from '../services/serviceClassificationService';

const ServiceClassificationContext = createContext(null);

export function ServiceClassificationProvider({ children }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceClassificationService.getFormData({ active_only: true });
      if (res.success && res.data && Array.isArray(res.data.hierarchy)) {
        setHierarchy(res.data.hierarchy);
      } else {
        setHierarchy([]);
        setError(res.message || 'No se pudo cargar la clasificación de servicios');
      }
    } catch (e) {
      setHierarchy([]);
      setError(e.response?.data?.message || e.message || 'Error al cargar la clasificación de servicios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(
    () =>
      hierarchy.map((c) => ({
        id: c.id,
        code: c.code,
        name: c.name,
        description: c.description,
        is_active: c.is_active,
      })),
    [hierarchy]
  );

  const subcategories = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).map((s) => ({
          ...s,
          category: { id: c.id, code: c.code, name: c.name },
          category_id: s.category_id ?? c.id,
          service_category_id: s.service_category_id ?? c.id,
        }))
      ),
    [hierarchy]
  );

  const families = useMemo(
    () =>
      hierarchy.flatMap((c) =>
        (c.subcategories || []).flatMap((s) =>
          (s.families || []).map((f) => ({
            ...f,
            subcategory: {
              id: s.id,
              code: s.code,
              name: s.name,
              service_subcategory_id: s.id,
              category_id: c.id,
              service_category_id: c.id,
            },
          }))
        )
      ),
    [hierarchy]
  );

  const value = useMemo(
    () => ({
      hierarchy,
      loading,
      error,
      refetch: load,
      categories,
      subcategories,
      families,
    }),
    [hierarchy, loading, error, load, categories, subcategories, families]
  );

  return (
    <ServiceClassificationContext.Provider value={value}>
      {children}
    </ServiceClassificationContext.Provider>
  );
}

export function useServiceClassification() {
  const ctx = useContext(ServiceClassificationContext);
  if (!ctx) {
    throw new Error('useServiceClassification debe usarse dentro de ServiceClassificationProvider');
  }
  return ctx;
}

export default ServiceClassificationContext;
